import { Trans, useLingui } from '@lingui/react/macro'
import { SizedPlot } from 'features/timeSeries/experiment/SizedPlot'
import {
  MethodResult,
  TrainResponse,
} from 'features/timeSeries/experiment/types'
import { Layout, PlotData } from 'plotly.js'
import { FC, ReactNode, useMemo } from 'react'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'
import { mobileInputTextStyles } from 'styles/mobileInputTextStyles'

const FG = CantileverTheme.colors.grayscale['100']
const GRID = CantileverTheme.colors.grayscale['600']
const METHOD_COLORS = [
  '#22d3ee',
  '#f97316',
  '#ef4444',
  CantileverTheme.colors.green['400'],
]

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
`

const Title = styled.h3`
  margin: 0;
  font-size: 0.95rem;
`

const Callout = styled.div`
  padding: 0.55rem 0.7rem;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  background: ${({ theme }) => theme.forecast.background2};
  font-size: 0.75rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const Select = styled.select`
  padding: 0.3rem 0.45rem;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 6px;
  background: ${({ theme }) => theme.forecast.pageBackground1};
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 0.78rem;
  max-width: 22rem;
  ${mobileInputTextStyles}
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.75rem;
  th,
  td {
    text-align: left;
    padding: 0.25rem 0.4rem;
    border-bottom: 1px solid ${({ theme }) => theme.forecast.border};
  }
  th {
    color: ${({ theme }) => theme.forecast.secondaryText};
    font-weight: 500;
  }
`

const Sub = styled.h4`
  margin: 0.4rem 0 0.25rem;
  font-size: 0.82rem;
`

const fmt = (v: number | null | undefined, digits = 4) =>
  v === null || v === undefined || !Number.isFinite(v) ? '—' : v.toFixed(digits)

export interface DiagnosticsPanelProps {
  /**
   * Structural rather than the full `TrainResponse` so a sentinel-design run
   * renders through the same diagnostics. Only the target, the fitted methods,
   * the training summary and the warnings are read.
   */
  result: Pick<TrainResponse, 'target' | 'methods'> &
    Partial<Pick<TrainResponse, 'training' | 'warnings'>>
  /**
   * Selected method key, lifted to the caller: the same selection drives the
   * expected/residual band overlay on the main viewer chart.
   */
  methodKey: string
  onMethodKeyChange: (key: string) => void
  /** Title above the panel; the lab's default names the train step. */
  title?: string
  /**
   * Where the actual/expected series is drawn, when the host has a chart to
   * draw it on. Omitted by hosts that do not (the sentinel-design flow).
   */
  overlayNote?: ReactNode
}

export const DiagnosticsPanel: FC<DiagnosticsPanelProps> = ({
  result,
  methodKey,
  onMethodKeyChange,
  title,
  overlayNote,
}) => {
  const { t } = useLingui()
  const displayTitle = title ?? t`Train results`

  const methods = Object.entries(result.methods)
  const method: MethodResult | undefined = result.methods[methodKey]
  const color =
    METHOD_COLORS[
      Math.max(
        0,
        methods.findIndex(([k]) => k === methodKey)
      ) % METHOD_COLORS.length
    ]
  const target = result.target
  const band =
    method?.per_tag?.[target]?.residual_band ??
    method?.band_meta?.residual_band ??
    NaN

  const lossChart = useMemo(() => {
    const curve = method?.loss_curve
    const train = curve?.train_loss
    if (!train?.length) return null
    const iterations = curve?.n_iter ?? train.length
    const epochs = train.map((_, i) => i + 1)
    const data: Partial<PlotData>[] = [
      {
        x: epochs,
        y: train,
        type: 'scatter',
        mode: 'lines',
        name: 'train loss',
        line: { width: 1.4, color },
      },
    ]
    const val = curve?.val_loss?.length
      ? curve.val_loss
      : curve?.validation_loss_proxy
    if (val?.length) {
      data.push({
        x: val.map((_, i) => i + 1),
        y: val,
        type: 'scatter',
        mode: 'lines',
        name: 'val loss',
        line: { width: 1.2, color: '#f97316' },
      })
    }
    const layout: Partial<Layout> = {
      paper_bgcolor: 'rgba(0,0,0,0)',
      plot_bgcolor: 'rgba(0,0,0,0)',
      margin: { l: 48, r: 12, t: 28, b: 36 },
      legend: { font: { color: FG, size: 10 } },
      title: {
        text: t`MLP loss curve (${iterations} iters)`,
        font: { color: FG, size: 12 },
        x: 0,
      },
      xaxis: {
        title: { text: 'epoch', font: { color: FG, size: 10 } },
        tickfont: { color: FG, size: 10 },
        gridcolor: GRID,
      },
      yaxis: {
        title: { text: 'loss', font: { color: FG, size: 10 } },
        tickfont: { color: FG, size: 10 },
        gridcolor: GRID,
      },
    }
    return { data, layout }
  }, [method?.loss_curve, color, t])

  const knnChart = useMemo(() => {
    const insp = method?.knn_inspection
    if (!insp?.library_scatter?.length) return null
    const lib: Partial<PlotData> = {
      x: insp.library_scatter.map((p) => p.x),
      y: insp.library_scatter.map((p) => p.y),
      type: 'scatter',
      mode: 'markers',
      name: 'library',
      marker: {
        size: 4,
        color: insp.library_scatter.map((p) => p.target),
        // Plotly colorscale identifier; translating it would change the palette.
        // eslint-disable-next-line lingui/no-unlocalized-strings -- Plotly expects this exact identifier.
        colorscale: 'Viridis',
        opacity: 0.55,
        showscale: true,
        colorbar: { title: { text: 'y', font: { color: FG, size: 10 } } },
      },
    }
    const data: Partial<PlotData>[] = [lib]
    const librarySize = insp.library_size
    const example = insp.examples[0]
    if (example) {
      data.push({
        x: [example.xy[0]],
        y: [example.xy[1]],
        type: 'scatter',
        mode: 'markers',
        name: 'query',
        marker: { size: 11, color: '#ef4444', symbol: 'x' },
      })
      data.push({
        x: example.neighbors.map((n) => n.xy[0]),
        y: example.neighbors.map((n) => n.xy[1]),
        type: 'scatter',
        mode: 'markers',
        name: `k=${insp.k} neighbors`,
        marker: {
          size: 9,
          color: '#22d3ee',
          symbol: 'circle-open',
          line: { width: 2, color: '#22d3ee' },
        },
        text: example.neighbors.map(
          (n) =>
            t`d=${{ value217_19: n.distance ?? '—' }} · y=${{ value217_44: n.neighbor_y ?? '—' }} · shared=${{ value217_76: n.n_shared }}`
        ),
        hoverinfo: 'text',
      })
    }
    const layout: Partial<Layout> = {
      paper_bgcolor: 'rgba(0,0,0,0)',
      plot_bgcolor: 'rgba(0,0,0,0)',
      margin: { l: 48, r: 12, t: 28, b: 40 },
      legend: { font: { color: FG, size: 10 } },
      title: {
        text: t`kNN library (${librarySize} pts) — first example neighbors`,
        font: { color: FG, size: 12 },
        x: 0,
      },
      xaxis: {
        title: {
          text: insp.xy_axes[0] || 'x',
          font: { color: FG, size: 10 },
        },
        tickfont: { color: FG, size: 10 },
        gridcolor: GRID,
      },
      yaxis: {
        title: {
          text: insp.xy_axes[1] || 'y',
          font: { color: FG, size: 10 },
        },
        tickfont: { color: FG, size: 10 },
        gridcolor: GRID,
      },
    }
    return { data, layout, examples: insp.examples }
  }, [method?.knn_inspection, t])

  // Time-series diagnostics (actual vs expected, residual trace) render on
  // the main viewer chart as uPlot overlays; only the non-time-indexed
  // residual histogram stays as a Plotly card here.
  const histChart = useMemo(() => {
    if (!method?.residual_histogram) return null
    const layout: Partial<Layout> = {
      paper_bgcolor: 'rgba(0,0,0,0)',
      plot_bgcolor: 'rgba(0,0,0,0)',
      margin: { l: 48, r: 12, t: 28, b: 28 },
      legend: { font: { color: FG, size: 10 } },
      title: {
        text: t`Residual histogram`,
        font: { color: FG, size: 12 },
        x: 0,
      },
      xaxis: { tickfont: { color: FG, size: 10 }, gridcolor: GRID },
      yaxis: { tickfont: { color: FG, size: 10 }, gridcolor: GRID },
    }
    const data: Partial<PlotData>[] = [
      {
        x: method.residual_histogram.bins,
        y: method.residual_histogram.counts,
        type: 'bar' as const,
        marker: { color },
        name: 'residual hist',
      },
    ]
    return { data, layout }
  }, [method?.residual_histogram, color, t])

  if (!method) return null

  return (
    <Wrap>
      <Title>{displayTitle}</Title>
      {result.warnings?.map((w) => (
        <Callout key={w} style={{ color: '#f97316' }}>
          {w}
        </Callout>
      ))}
      <Select
        value={methodKey}
        onChange={(e) => onMethodKeyChange(e.target.value)}>
        {methods.map(([key, m]) => (
          <option key={key} value={key}>
            {m.label}
          </option>
        ))}
      </Select>
      <Callout>
        <strong>
          <Trans>How the band is chosen:</Trans>
        </strong>{' '}
        {method.band_meta ? (
          <>
            <Trans>constant ±</Trans>
            {fmt(method.band_meta.residual_band, 4)}
            <Trans> = k (</Trans>
            {method.band_meta.band_k}
            <Trans>) × err_scale (</Trans>
            {fmt(method.band_meta.err_scale, 4)}
            <Trans>) where err_scale =</Trans> {method.band_meta.estimator}
            {method.band_meta.estimator === 'abs_quantile'
              ? t` (q=${{ value315_25: method.band_meta.band_quantile }})`
              : ''}{' '}
            <Trans>of |residual| on </Trans>
            {method.band_meta.calib_rows}
            <Trans> calibration rows (</Trans>
            {method.band_meta.calib_source === 'explicit_val'
              ? t`your Val timeline chunks`
              : t`spread blocks carved from Train`}
            <Trans>). Not per-day or per-point — one scalar per model.</Trans>
          </>
        ) : (
          <>
            ±{fmt(band, 4)}
            <Trans> around expected / zero residual.</Trans>
          </>
        )}
      </Callout>
      {result.training?.validation_note && (
        <Callout>
          <strong>
            <Trans>Validation:</Trans>
          </strong>{' '}
          {result.training.validation_note}
        </Callout>
      )}
      <Sub>
        <Trans>Metrics by split role</Trans>
      </Sub>
      <Table>
        <thead>
          <tr>
            <th>
              <Trans>Role</Trans>
            </th>
            <th>
              <Trans>Rows</Trans>
            </th>
            <th>MAE</th>
            <th>RMSE</th>
            <th>
              <Trans>R²</Trans>
            </th>
            <th>
              <Trans>Drift</Trans>
            </th>
            <th>
              <Trans>Band cov.</Trans>
            </th>
            <th>
              <Trans>MAE/scale</Trans>
            </th>
          </tr>
        </thead>
        <tbody>
          {(['train', 'val', 'test'] as const).map((role) => {
            const m = method.metrics_by_role?.[role]
            if (!m) return null
            return (
              <tr key={role}>
                <td>{role}</td>
                <td>{m.rows}</td>
                <td>{fmt(m.mae)}</td>
                <td>{fmt(m.rmse)}</td>
                <td>{fmt(m.r2, 3)}</td>
                <td>{fmt(m.drift)}</td>
                <td>
                  {m.band_coverage === null || m.band_coverage === undefined
                    ? '—'
                    : `${(m.band_coverage * 100).toFixed(1)}%`}
                </td>
                <td>{fmt(m.score_mae_scaled, 3)}</td>
              </tr>
            )
          })}
        </tbody>
      </Table>
      {lossChart && (
        <SizedPlot
          $heightRem={12}
          data={lossChart.data}
          layout={lossChart.layout}
        />
      )}
      {knnChart && (
        <>
          <SizedPlot
            $heightRem={14}
            data={knnChart.data}
            layout={knnChart.layout}
          />
          <Sub>
            <Trans>kNN neighbor picks (sample queries)</Trans>
          </Sub>
          <Table>
            <thead>
              <tr>
                <th>
                  <Trans>Query ts</Trans>
                </th>
                <th>
                  <Trans>Actual y</Trans>
                </th>
                <th>
                  <Trans>Neighbor ys (nearest→)</Trans>
                </th>
                <th>
                  <Trans>Distances</Trans>
                </th>
              </tr>
            </thead>
            <tbody>
              {knnChart.examples.map((ex) => (
                <tr key={ex.ts}>
                  <td>{ex.ts.slice(0, 19)}</td>
                  <td>{fmt(ex.actual_y)}</td>
                  <td>
                    {ex.neighbors.map((n) => fmt(n.neighbor_y, 3)).join(', ')}
                  </td>
                  <td>
                    {ex.neighbors.map((n) => fmt(n.distance, 3)).join(', ')}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}
      {histChart && (
        <SizedPlot
          $heightRem={10}
          data={histChart.data}
          layout={histChart.layout}
        />
      )}
      {overlayNote && <Callout>{overlayNote}</Callout>}
      {result.training && (
        <Callout>
          <Trans>Training: </Trans>
          {result.training.rows}
          <Trans> rows · train=</Trans>
          {result.training.train_rows}
          <Trans> · val=</Trans>
          {result.training.val_rows ?? 0}
          <Trans> · test=</Trans>
          {result.training.score_rows}
          <Trans> · split_date=</Trans>
          {result.training.split_date}
          <br />
          {result.training.split_policy}
        </Callout>
      )}
    </Wrap>
  )
}
